import { createAsyncThunk, createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';

import { RootState } from 'app/store';
import { ApiResListsType } from 'global-services/api/OhsApiModels';
import OhsDocumentsListsRecord from 'documents/models/OhsDocumentsRecord';
import OhsUserLocalServices from 'user/OhsUserLocalServices';
import { OhsUser } from 'user/OhsUser';
import { OhsTabKeyTypes } from 'global-components/tab/OhsTabModels';

import {
  getDocumentsLists,
  getCopiedDocumentsLists,
  getDocumentsAllocatedList,
  getDocumentsSearchLists,
  getCopiedDocumentsSearchLists,
} from './OhsDocumentsServices';
import parseListAllocations from './utils/OhsParseListAllocations';
import { TierType } from '../global-services/constants/OhsObject';
import { listActiveTaskFor } from '../task/OhsTaskServices';
import { OhsDocumentFilterPayload, OhsDocumentSearchMain } from './models/OhsDocumentsModels';

const defaultDocumentLists = {
  items: [],
  pagination: {
    page: 0,
    totalPages: 0,
    next: '',
  },
};

export interface DocumentsListsRecordState {
  isLoading: boolean;
  documentsList: ApiResListsType<any[]> | null;
  currentPage: number;
}

const initialState: DocumentsListsRecordState = {
  isLoading: false,
  documentsList: defaultDocumentLists,
  currentPage: 1,
};

export const getViewPreset = (tierNum: number) => {
  switch (tierNum) {
    case 2:
      return 'view_1';
    case 3:
      return 'view_2';
    case 4:
    case 5:
      return 'view_4';
    default:
      return undefined;
  }
};

const fetchAllocations = async (documentsList: any): Promise<ApiResListsType<any[]> | null> => {
  if (documentsList && documentsList?.items.length === 0) {
    return new Promise((resolve) => {
      resolve(documentsList);
    });
  }

  // get allocated records from documents _ids
  const getIDs: string[] = [];
  documentsList?.items.forEach((item: OhsDocumentsListsRecord) => {
    if (item._id && item.tier?.type === TierType.T3) getIDs.push(item._id);
  });
  if (getIDs && getIDs.length === 0) {
    return new Promise((resolve) => {
      resolve(documentsList);
    });
  }
  const allocatedList = await getDocumentsAllocatedList(getIDs);

  // map allocated records to safetylist
  const documentsListWithAllocations = parseListAllocations(
    documentsList,
    allocatedList
  ) as ApiResListsType<OhsDocumentsListsRecord[]> | null;

  return new Promise((resolve) => {
    resolve(documentsListWithAllocations);
  });
};

const getDocumentsWithAllocations = (
  filters: OhsDocumentFilterPayload | OhsDocumentSearchMain,
  hasSearch: boolean = false
): Promise<ApiResListsType<any[]> | null> => {
  const getDocumentsListReq = async (): Promise<ApiResListsType<any[]> | null> => {
    const documentsList = hasSearch
      ? await getDocumentsSearchLists(filters as OhsDocumentSearchMain)
      : await getDocumentsLists(filters as OhsDocumentFilterPayload);

    // exit if list result is empty
    const res = fetchAllocations(documentsList);
    return res;
  };

  return getDocumentsListReq();
};

const documentsListsRPC = async (
  filters: OhsDocumentFilterPayload | OhsDocumentSearchMain,
  tab: OhsTabKeyTypes.Documents | OhsTabKeyTypes.Workplace | OhsTabKeyTypes.Template,
  hasSearch: boolean = false
): Promise<any> => {
  const user = OhsUserLocalServices.getLocalOhsUser() as OhsUser;
  const userTier = user?.tierNum ?? 0;

  let response: any = null;

  // Template
  if (tab === OhsTabKeyTypes.Template && user?.configs.vdoc?.PERMISSIONS.view) {
    response = hasSearch
      ? await getCopiedDocumentsSearchLists(filters as OhsDocumentSearchMain)
      : await getCopiedDocumentsLists(filters as OhsDocumentFilterPayload);
    return response;
  }

  if (userTier && userTier === 3 && user?.configs.vdoc?.PERMISSIONS.view) {
    response = await getDocumentsWithAllocations(filters, hasSearch);
  }
  if (
    (userTier && userTier === 2 && user?.configs.vdoc?.PERMISSIONS.view) ||
    (userTier && (userTier === 4 || userTier === 5) && user?.configs.vdoc?.PERMISSIONS.view)
  ) {
    response = hasSearch
      ? await getDocumentsSearchLists(filters as OhsDocumentSearchMain)
      : await getDocumentsLists(filters as OhsDocumentFilterPayload);
  }

  if (
    response &&
    filters.viewPreset !== 'view_3' &&
    tab === OhsTabKeyTypes.Documents &&
    (userTier === 3 || userTier === 4)
  ) {
    const taskList = await listActiveTaskFor(response.items.map((item: any) => item._id));
    response.items.forEach((item: any) => {
      item.review = taskList.find((t) => t._id === item._id);
    });
  }

  return response;
};

export const fetchDocumentsListsAsync = createAsyncThunk<
  {
    list: ApiResListsType<OhsDocumentsListsRecord[]> | null;
    count: ApiResListsType<OhsDocumentsListsRecord[]> | null;
  },
  {
    list: OhsDocumentFilterPayload | OhsDocumentSearchMain;
    count: OhsDocumentFilterPayload | OhsDocumentSearchMain;
    hasSearch: boolean;
    tab: OhsTabKeyTypes.Documents | OhsTabKeyTypes.Workplace | OhsTabKeyTypes.Template;
  },
  { state: RootState }
>('actions/fetchDocumentsList', async (payload, thunkAPI) => {
  try {
    const [list, count] = await Promise.all([
      payload.hasSearch
        ? documentsListsRPC(payload.list as OhsDocumentSearchMain, payload.tab, true)
        : documentsListsRPC(payload.list as OhsDocumentFilterPayload, payload.tab),
      payload.hasSearch
        ? documentsListsRPC(payload.count as OhsDocumentSearchMain, payload.tab, true)
        : documentsListsRPC(payload.count as OhsDocumentFilterPayload, payload.tab),
    ]);

    return { list, count };
  } catch (err: any) {
    return thunkAPI.rejectWithValue(err.response.data);
  }
});

export const documentsSlice = createSlice({
  name: 'documents',
  initialState,
  reducers: {
    setDocumentsCurrentPage: (state, action: PayloadAction<number>) => {
      state.currentPage = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchDocumentsListsAsync.pending, (state) => {
        state.isLoading = true;
        state.documentsList = {
          items: [],
          pagination: state.documentsList?.pagination ?? {
            page: 0,
            totalPages: 0,
            next: '',
          },
        };
      })
      .addCase(fetchDocumentsListsAsync.fulfilled, (state, action) => {
        if (action.payload.list) {
          state.documentsList = action.payload.list;
          let setItemsLastId = '';
          const documentItems = [...state.documentsList.items];
          if (documentItems.length > 0) setItemsLastId = documentItems?.pop()?._id || '';

          state.documentsList.pagination.next = setItemsLastId;
          state.isLoading = false;
        }
        if (action.payload.count && state.documentsList) {
          state.documentsList.pagination.totalPages =
            action.payload.count.pagination.totalPages ?? 0;
        }
      })
      .addCase(fetchDocumentsListsAsync.rejected, (state) => {
        state.documentsList = null;
      });
  },
});

const documentsState = (state: RootState) => state.documents;
// Memoized Selectors
export const getOhsDocumentsModuleState = createSelector(
  [documentsState],
  (documents) => documents
);
export const { setDocumentsCurrentPage } = documentsSlice.actions;
export const documentsModuleReducer = documentsSlice.reducer;
